import { BarChart3, Cpu, FolderOpen, GitCommitHorizontal, Maximize2, Minus, X } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { ChatBody, DEFAULT_BRAND, type Harness, type PinnedAgent } from './ChatBody.tsx';
import { OPEN_CHAT_EVENT, OPEN_HARNESS_EVENT } from './events.ts';
import { useChatHost } from './host.tsx';
import { getTopZ } from './zOrder.ts';

// The in-app chat WINDOW, PORTED one to one from the old platform's AssistantChat.jsx
// (the orchestrator harness): the launcher, the floating window (drag by the title bar, eight resize
// handles, minimize, close; Standard mode adds maximize, opens bottom-left and auto-opens once per tab),
// and — with an app open — the launcher becomes the app's companion speed dial. What was the chat.html
// iframe is ChatBody (the same DOM and CSS, no iframe). The window is hidden, never unmounted, once it
// has been opened: the transcript, a turn in progress and the draft survive close and collapse.

const MIN_W = 560;
const MIN_H = 400;
const DEFAULT_W = 720;
const DEFAULT_H = 600;
/** Each harness's chrome (AssistantChat.jsx HARNESS_CONFIG). */
const HARNESS_CONFIG = {
  orchestrator: { label: DEFAULT_BRAND, right: 24, icon: Cpu, titleBarColor: '#3c5d88', hideButton: false },
  // No standing launcher: the engine window opens only on an OPEN_HARNESS_EVENT {harness:'engine'}.
  // (The old window's Flow endpoint-readiness popover is not here: it showed only Flow endpoints.)
  engine: { label: 'Pipeline engine', right: 76, icon: Cpu, titleBarColor: '#6d28d9', hideButton: true },
  analyst: {
    label: 'Data analyst',
    right: 128,
    icon: BarChart3,
    titleBarColor: '#15803d',
    hideButton: false,
  },
} as const;

export { OPEN_HARNESS_EVENT };

export function ChatPanel({
  slug,
  app = null,
  harness = 'orchestrator',
}: {
  slug: string;
  /** The open app (apps/:viewName): the launcher becomes its companion speed dial, as in the old platform. */
  app?: string | null;
  /** The orchestrator window (default) or the Pipeline engine window. */
  harness?: 'orchestrator' | 'engine';
}) {
  const host = useChatHost();
  const simpleView = host.simple ?? false;
  const simple = simpleView && harness === 'orchestrator';
  // Standard-mode chat = the orchestrator window in the simple view: left-side launcher, auto-open.
  const standardChat = simple;
  // WHICH chat an app's Chat companion opens (its declaration's chat_kind): the orchestrator window
  // of an app published with the ANALYST chat is the Data analyst (title, icon, colour, harness).
  const [chatKind, setChatKind] = React.useState<'assistant' | 'analyst'>('assistant');
  // Read again when the app's viewer options are saved (Assistant ⇄ Analyst shows without a reload).
  const [kindRev, setKindRev] = React.useState(0);
  React.useEffect(
    () => host.onCompanionsChanged?.(slug, app, () => setKindRev((n) => n + 1)),
    [host, slug, app],
  );
  React.useEffect(() => {
    setChatKind('assistant');
    if (harness !== 'orchestrator' || !app || !host.chatKindOf) return;
    let alive = true;
    void host
      .chatKindOf(slug, app)
      .then((k) => alive && setChatKind(k === 'analyst' ? 'analyst' : 'assistant'))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [harness, app, slug, host, kindRev]);
  // WHO draws this app's chat: 'app' — the app's own chat UI (the headless chat) — and no window here.
  // Until the host knows ('pending', a slow network can take seconds), nothing of the host's chat shows for an
  // app: no dial offering a Chat that the app may draw itself, no window to open (customer report, 2026-10-08).
  const asksChatUi = harness === 'orchestrator' && !!app && !!host.chatUiOf;
  const [chatUi, setChatUi] = React.useState<'host' | 'app' | 'pending'>(asksChatUi ? 'pending' : 'host');
  React.useEffect(() => {
    if (!asksChatUi || !app || !host.chatUiOf) {
      setChatUi('host');
      return;
    }
    setChatUi('pending');
    let alive = true;
    void host
      .chatUiOf(slug, app)
      .then((u) => alive && setChatUi(u === 'app' ? 'app' : 'host'))
      // The app's declaration cannot be read: the host's own chat, as before the option existed.
      .catch(() => alive && setChatUi('host'));
    return () => {
      alive = false;
    };
  }, [asksChatUi, app, slug, host, kindRev]);
  const appDrawsChat = !!app && harness === 'orchestrator' && chatUi === 'app';
  const chatUiPending = chatUi === 'pending';
  const appDrawsChatRef = React.useRef(appDrawsChat || chatUiPending);
  appDrawsChatRef.current = appDrawsChat || chatUiPending;
  const bodyHarness: Harness =
    harness === 'engine' ? 'engine' : chatKind === 'analyst' ? 'analyst' : 'orchestrator';
  const HARNESS =
    bodyHarness === 'orchestrator'
      ? { ...HARNESS_CONFIG.orchestrator, label: host.brand ?? DEFAULT_BRAND }
      : HARNESS_CONFIG[bodyHarness];
  const HarnessIcon = HARNESS.icon;
  const [isOpen, setIsOpen] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(false);
  const [everOpened, setEverOpened] = React.useState(false);
  React.useEffect(() => {
    if (isOpen) setEverOpened(true);
  }, [isOpen]);
  const [size, setSize] = React.useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [pos, setPos] = React.useState<{ x: number; y: number } | null>(null);
  const [isMaximized, setIsMaximized] = React.useState(false);
  const prevBounds = React.useRef<{ pos: { x: number; y: number }; size: { w: number; h: number } } | null>(
    null,
  );
  const [zIndex, setZIndex] = React.useState(() => getTopZ());
  const bodyRef = React.useRef<HTMLDivElement>(null);
  // A prompt to seed into the input, and a project agent to switch the chat to (OPEN_CHAT_EVENT).
  const [seed, setSeed] = React.useState<{ text: string; n: number } | null>(null);
  const [agent, setAgent] = React.useState<PinnedAgent | null>(null);

  // First open: bottom-right normally; Standard mode opens bottom-LEFT of the content area (past the
  // 256px sidebar) so the window grows out of its left-side launcher.
  React.useEffect(() => {
    if (isOpen && pos === null)
      setPos({
        x: standardChat
          ? Math.max(8, Math.min(280, window.innerWidth - DEFAULT_W - 24))
          : Math.max(0, window.innerWidth - DEFAULT_W - 24),
        y: Math.max(0, window.innerHeight - DEFAULT_H - 24),
      });
  }, [isOpen, pos, standardChat]);

  // Standard mode: the chat is the product's front door — auto-open it once per tab session; a
  // deliberate close sets the flag so it stays closed until a new tab.
  React.useEffect(() => {
    if (!standardChat || harness !== 'orchestrator') return;
    try {
      if (sessionStorage.getItem('cortex_chat_dismissed') === '1') return;
    } catch {
      /* storage unavailable: still auto-open */
    }
    setIsOpen(true);
  }, [standardChat]);
  const closeChat = React.useCallback(() => {
    try {
      sessionStorage.setItem('cortex_chat_dismissed', '1');
    } catch {
      /* best effort */
    }
    setIsOpen(false);
  }, []);

  // Another page opens the chat (Apps "Ask the agent", the dial's Chat, an agent row) with a draft
  // and/or a project agent to switch to.
  React.useEffect(() => {
    const on = (e: Event) => {
      // The app draws its own chat: nothing opens here.
      if (appDrawsChatRef.current) return;
      const d = (e as CustomEvent<{ draft?: string; agent?: PinnedAgent | null }>).detail ?? {};
      if (d.draft) setSeed((s) => ({ text: d.draft!, n: (s?.n ?? 0) + 1 }));
      if ('agent' in d) setAgent(d.agent ?? null);
      setIsOpen(true);
      setIsMinimized(false);
    };
    // OPEN_HARNESS_EVENT: only the window whose harness matches reacts (none named: the
    // orchestrator).
    const onHarness = (e: Event) => {
      const d =
        (e as CustomEvent<{ harness?: string; initialPrompt?: string; selectedAgent?: PinnedAgent | null }>)
          .detail ?? {};
      if ((d.harness ?? 'orchestrator') !== harness) return;
      if (appDrawsChatRef.current) return;
      if (d.initialPrompt) setSeed((s) => ({ text: d.initialPrompt!, n: (s?.n ?? 0) + 1 }));
      if ('selectedAgent' in d) setAgent(d.selectedAgent ?? null);
      setIsOpen(true);
      setIsMinimized(false);
    };
    if (harness === 'orchestrator') window.addEventListener(OPEN_CHAT_EVENT, on);
    window.addEventListener(OPEN_HARNESS_EVENT, onHarness);
    return () => {
      window.removeEventListener(OPEN_CHAT_EVENT, on);
      window.removeEventListener(OPEN_HARNESS_EVENT, onHarness);
    };
  }, [harness]);

  // Drag to move via the title bar, and resize via the edges/corners: POINTER CAPTURE, so a release
  // over an app's iframe still ends the gesture (the e.buttons === 0 guard self-heals a lost capture).
  const gesture = (e: React.PointerEvent, onMove: (ev: PointerEvent) => void) => {
    const handle = e.currentTarget as HTMLElement;
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* capture unavailable */
    }
    const move = (ev: PointerEvent) => {
      if (ev.buttons === 0) return up(ev);
      onMove(ev);
    };
    const up = (ev: PointerEvent) => {
      try {
        handle.releasePointerCapture(ev.pointerId);
      } catch {
        /* already released */
      }
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', up);
      handle.removeEventListener('pointercancel', up);
      document.body.style.userSelect = '';
      if (bodyRef.current) bodyRef.current.style.pointerEvents = '';
    };
    document.body.style.userSelect = 'none';
    if (bodyRef.current) bodyRef.current.style.pointerEvents = 'none';
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', up);
    handle.addEventListener('pointercancel', up);
  };
  const handleDragStart = (e: React.PointerEvent) => {
    if (!pos) return;
    e.preventDefault();
    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;
    gesture(e, (ev) =>
      setPos({
        x: Math.max(0, Math.min(window.innerWidth - size.w, ev.clientX - startX)),
        y: Math.max(0, Math.min(window.innerHeight - 48, ev.clientY - startY)),
      }),
    );
  };
  const handleResizeStart = (e: React.PointerEvent, direction: string) => {
    if (!pos) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;
    const startPosX = pos.x;
    const startPosY = pos.y;
    gesture(e, (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      let newW = startW;
      let newH = startH;
      let newX = startPosX;
      let newY = startPosY;
      if (direction.includes('e')) newW = Math.max(MIN_W, startW + dx);
      if (direction.includes('w')) {
        newW = Math.max(MIN_W, startW - dx);
        newX = startPosX + (startW - newW);
      }
      if (direction.includes('s')) newH = Math.max(MIN_H, startH + dy);
      if (direction.includes('n')) {
        newH = Math.max(MIN_H, startH - dy);
        newY = startPosY + (startH - newH);
      }
      setSize({ w: newW, h: newH });
      setPos({ x: newX, y: newY });
    });
  };

  // Mobile with an app open full-screen: the floating launcher would sit on the app's own controls.
  const mobileAppOpen =
    !!app && typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches;
  // Standard mode: a 56px round launcher bottom-LEFT of the content area; Advanced: 40px bottom-right.
  const toggleButton = (
    <button
      type="button"
      data-testid="chat-launcher"
      onClick={() => setIsOpen(true)}
      style={standardChat ? undefined : { right: HARNESS.right }}
      data-tour={`chat-launcher-${harness}`}
      className={`fixed bottom-6 z-[99999] flex items-center justify-center cursor-pointer border-0 hover:scale-105 transition-all ${
        standardChat
          ? 'w-14 h-14 rounded-full shadow-xl left-6 md:left-[280px]'
          : 'w-10 h-10 rounded-xl shadow-lg'
      } bg-nav-active hover:bg-nav-active-dark`}
      title={`Open ${HARNESS.label}`}
    >
      <HarnessIcon className={standardChat ? 'w-7 h-7 text-white' : 'w-5 h-5 text-white'} />
    </button>
  );
  React.useEffect(() => {
    if (appDrawsChat) setIsOpen(false);
  }, [appDrawsChat]);
  // With an app open the launcher IS the companion speed dial ("the dial IS the chat button now").
  const dial =
    app && harness === 'orchestrator' && host.dial ? host.dial({ slug, app, hidden: isOpen }) : null;
  const closedLauncher = HARNESS.hideButton || app || mobileAppOpen ? null : toggleButton;

  // Not known yet who draws the app's chat: nothing yet.
  if (chatUiPending) return null;
  // The app draws its own chat (chat_ui 'app'): only the dial (without Chat) stays; no window, no launcher.
  if (appDrawsChat)
    return dial ? createPortal(<React.Fragment key="dial">{dial}</React.Fragment>, document.body) : null;
  if (!isOpen && (!everOpened || pos === null))
    return createPortal(
      [
        closedLauncher ? <React.Fragment key="launcher">{closedLauncher}</React.Fragment> : null,
        dial ? <React.Fragment key="dial">{dial}</React.Fragment> : null,
      ],
      document.body,
    );
  if (pos === null) return null;

  // Hidden = closed but still mounted (display none: out of the layout, the tab order and the a11y tree).
  const hidden = !isOpen;
  const windowH = isMinimized ? 48 : size.h;
  const barBtn =
    'w-6 h-6 flex items-center justify-center rounded border-0 bg-transparent cursor-pointer text-white/60 hover:text-white transition-colors';

  const chatWindow = (
    <div
      onMouseDown={() => setZIndex(getTopZ())}
      data-chat-window={bodyHarness}
      data-testid="chat-window"
      aria-hidden={hidden ? 'true' : undefined}
      className="fixed flex flex-col overflow-hidden rounded-xl border border-[#C8D9EC] shadow-[0_8px_40px_0_var(--shadow-blue-md)]"
      style={{
        display: hidden ? 'none' : undefined,
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: windowH,
        minWidth: MIN_W,
        minHeight: isMinimized ? 48 : MIN_H,
        zIndex,
      }}
    >
      {/* Title bar */}
      <div
        onPointerDown={handleDragStart}
        style={{ backgroundColor: HARNESS.titleBarColor }}
        className="flex items-center gap-2 px-4 h-12 cursor-grab active:cursor-grabbing select-none flex-shrink-0"
      >
        <HarnessIcon className="w-4 h-4 shrink-0 text-white/70" data-testid="app-chat-icon" />
        <span className="text-sm font-medium truncate text-white/90 mr-2" data-testid="chat-title">
          {HARNESS.label}
        </span>
        {/* Standard mode keeps the title bar bare: the project lives in the chat's own status line. */}
        {!simple && (
          <>
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/80">
              <FolderOpen className="w-3 h-3" /> {slug}
            </span>
            {host.context && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                <GitCommitHorizontal className="w-3 h-3" /> {host.context}
              </span>
            )}
          </>
        )}
        <span className="flex-1" />
        {simple && !isMinimized && (
          <button
            type="button"
            data-testid="chat-maximize"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => {
              if (!isMaximized) {
                prevBounds.current = { pos, size };
                setPos({ x: 16, y: 16 });
                setSize({ w: window.innerWidth - 32, h: window.innerHeight - 32 });
                setIsMaximized(true);
              } else {
                const b = prevBounds.current;
                if (b) {
                  setPos(b.pos);
                  setSize(b.size);
                }
                setIsMaximized(false);
              }
            }}
            className={barBtn}
            title={isMaximized ? 'Restore size' : 'Maximize'}
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          data-testid="chat-minimize"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setIsMinimized((v) => !v)}
          className={barBtn}
          title={isMinimized ? 'Restore' : 'Minimize'}
        >
          {isMinimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          data-testid="chat-close"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={closeChat}
          className={barBtn}
          title="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* What was the chat.html iframe: mounted once, hidden (never unmounted) on collapse. */}
      <div
        ref={bodyRef}
        className="flex-1 flex flex-col min-h-0 border-0 bg-card-bg"
        style={isMinimized ? { display: 'none' } : undefined}
        title="AI Assistant"
      >
        <ChatBody
          slug={slug}
          simple={simple}
          harness={bodyHarness}
          {...(app && harness === 'orchestrator' ? { app } : {})}
          seed={seed}
          agent={agent}
          onUnpinAgent={() => setAgent(null)}
          onClose={closeChat}
          navigate={host.navigate}
          getToken={host.getToken}
        />
      </div>

      {/* Resize handles */}
      {!isMinimized && (
        <>
          <div
            onPointerDown={(e) => handleResizeStart(e, 'n')}
            className="absolute top-0 left-2 right-2 h-1 cursor-n-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 's')}
            className="absolute bottom-0 left-2 right-2 h-1 cursor-s-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'w')}
            className="absolute top-2 bottom-2 left-0 w-1 cursor-w-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'e')}
            className="absolute top-2 bottom-2 right-0 w-1 cursor-e-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'nw')}
            className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'ne')}
            className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'sw')}
            className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize"
          />
          <div
            onPointerDown={(e) => handleResizeStart(e, 'se')}
            className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize"
          />
        </>
      )}
    </div>
  );

  // ALWAYS the same array shape with STABLE KEYS, so the window stays one DOM node open or closed.
  return createPortal(
    [
      hidden && closedLauncher ? <React.Fragment key="launcher">{closedLauncher}</React.Fragment> : null,
      dial ? <React.Fragment key="dial">{dial}</React.Fragment> : null,
      <React.Fragment key="window">{chatWindow}</React.Fragment>,
    ],
    document.body,
  );
}
